import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import useApiRequest from '../../../lib/hooks/useRequest';
import { ErrorEntity, Error as ErrorI } from '../../root/context';
import { OfferConfirmDialogProps } from '../components/offer-confirm-dialog/offer-confirm-dialog';
import { SendOfferDialogProps } from '../components/send-offer-dialog/send-offer-dialog';
import { OFFER_CONFLICTS } from '../constants/offer-conflicts';
import { OfferConfirmAction } from '../constants/offer-confirm-action.enum';
import { SendOfferRequestDto } from '../types/request';
import { OfferFileUrlResponseDto, OfferMetaResponseDto, OfferResponseDto } from '../types/response';

export interface OfferActionTarget {
  _id: string;
  mail: string;
  /** Sent before – sending again replaces the link. */
  isSent: boolean;
}

// The offer changed meanwhile (sent, accepted, revoked, archived) – the error modal says so, the caller reloads.
const STALE_ERRORS = [
  ErrorEntity.OfferNotOpenConflict,
  ErrorEntity.OfferNotDraftConflict,
  ErrorEntity.OfferNotSentConflict,
  ErrorEntity.OfferArchivedConflict,
  ErrorEntity.OfferNotClosedConflict
];

const CONFIRM_REQUEST: Record<OfferConfirmAction, { method: 'DELETE' | 'PATCH'; path: (id: string) => string; success: string }> = {
  [OfferConfirmAction.DELETE]: { method: 'DELETE', path: (id) => `offers/admin/${id}`, success: 'Entwurf wurde gelöscht.' },
  [OfferConfirmAction.REVOKE]: { method: 'PATCH', path: (id) => `offers/admin/${id}/revoke`, success: 'Angebot wurde zurückgezogen.' },
  [OfferConfirmAction.ARCHIVE]: { method: 'PATCH', path: (id) => `offers/admin/${id}/archive`, success: 'Angebot wurde archiviert.' }
};

/** Send / resend, revoke, delete and archive with their dialogs – shared by the list and the offer detail. `onChanged` reloads the caller. */
export const useOfferActions = (onChanged: () => void | Promise<void>) => {
  const { apiRequest } = useApiRequest();

  // Link validity default + maximum for the send dialog, start dates for the detail preview.
  const [meta, setMeta] = useState<OfferMetaResponseDto | null>(null);
  const [sendTarget, setSendTarget] = useState<OfferActionTarget | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ target: OfferActionTarget; action: OfferConfirmAction } | null>(null);

  useEffect(() => {
    const fetchMeta = async () => {
      const res = await apiRequest<OfferMetaResponseDto>('offers/admin/meta', 'GET');
      if (res.data) setMeta(res.data);
    };
    fetchMeta();
  }, [apiRequest]);

  const closeSendDialog = () => {
    setSendTarget(null);
    setSendError(null);
  };

  const handleSendConfirm = async (validityDays: number): Promise<boolean> => {
    if (!sendTarget) return true;
    setSendError(null);
    const body: SendOfferRequestDto = { validityDays };
    const res = await apiRequest<OfferResponseDto>(`offers/admin/${sendTarget._id}/send`, 'POST', { body });
    if (res.data) {
      toast.success('Angebot wurde versendet.');
      await onChanged();
      return true;
    }
    const error = res.error as ErrorI | undefined;
    if (error && STALE_ERRORS.includes(error.entity)) {
      await onChanged();
      return true;
    }
    // The error modal leaves these out – there is no field here, so the dialog shows them.
    const conflict = error?.entity ? OFFER_CONFLICTS[error.entity] : undefined;
    if (conflict) setSendError(conflict.message);
    else if (!error) toast.error('Es ist ein Fehler aufgetreten');
    return false;
  };

  const handleConfirm = async (): Promise<boolean> => {
    if (!confirmTarget) return true;
    const request = CONFIRM_REQUEST[confirmTarget.action];
    // DELETE answers 204 – `data` is then the string 'success'.
    const res = await apiRequest(request.path(confirmTarget.target._id), request.method);
    if (res.data) {
      toast.success(request.success);
      await onChanged();
      return true;
    }
    const error = res.error as ErrorI | undefined;
    if (error && STALE_ERRORS.includes(error.entity)) {
      await onChanged();
      return true;
    }
    if (!error) toast.error('Es ist ein Fehler aufgetreten');
    return false;
  };

  // The link is short-lived, so it is requested on click instead of with the offer.
  const openOfferFile = async (offerId: string) => {
    const res = await apiRequest<OfferFileUrlResponseDto>(`offers/admin/${offerId}/offer-file`, 'GET');
    if (res.data) window.open(res.data.url, '_blank', 'noopener,noreferrer');
  };

  const sendDialogProps: SendOfferDialogProps | null = meta && {
    candidate: sendTarget && { mail: sendTarget.mail, isResend: sendTarget.isSent },
    defaultValidityDays: meta.defaultValidityDays,
    maxValidityDays: meta.maxValidityDays,
    error: sendError,
    onClose: closeSendDialog,
    onConfirm: handleSendConfirm
  };

  const confirmDialogProps: OfferConfirmDialogProps = {
    candidate: confirmTarget && { mail: confirmTarget.target.mail, action: confirmTarget.action },
    onClose: () => setConfirmTarget(null),
    onConfirm: handleConfirm
  };

  return {
    meta,
    openSend: setSendTarget,
    openOfferFile,
    openConfirm: (target: OfferActionTarget, action: OfferConfirmAction) => setConfirmTarget({ target, action }),
    sendDialogProps,
    confirmDialogProps
  };
};
